import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLidarClearance } from "./LidarClearanceContext";
import { copyAxisLabels, type LidarClearanceLabel } from "./labelsApi";
import type { LidarClearanceAxis } from "./schemas";
import t from "./translations";

type Props = {
  open: boolean;
  systemId: number;
  region: string;
  axes: LidarClearanceAxis[];
  onClose: () => void;
  onCopied: (labels: LidarClearanceLabel[]) => void;
};

export default function CopyLabelsDialog({
  open,
  systemId,
  region,
  axes,
  onClose,
  onCopied,
}: Props) {
  const { systems, systemAxes } = useLidarClearance();
  const [axisId, setAxisId] = useState<number | "">("");
  const [sourceSystemId, setSourceSystemId] = useState<number | "">("");
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (open) {
      setAxisId("");
      setSourceSystemId("");
    }
  }, [open]);

  const selectAxis = (nextAxisId: number) => {
    setAxisId(nextAxisId);
    setSourceSystemId(""); // the previous axis's source system may not link the new one
  };

  // Axis/system ids are only unique within one region's database - a source
  // system can only share an axis by id with systems in that same region.
  const sourceOptions = useMemo(() => {
    if (axisId === "") return [];
    const sourceSystemIds = new Set(
      systemAxes
        .filter(
          (link) =>
            link.region === region &&
            link.axis_id === axisId &&
            link.system_id !== systemId
        )
        .map((link) => link.system_id)
    );
    return systems
      .filter(
        (system) => system.region === region && sourceSystemIds.has(system.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [systemAxes, systems, region, axisId, systemId]);

  const canCopy = axisId !== "" && sourceSystemId !== "" && !copying;

  const handleCopy = async () => {
    if (axisId === "" || sourceSystemId === "") return;
    setCopying(true);
    try {
      const created = await copyAxisLabels(
        region,
        systemId,
        axisId,
        sourceSystemId
      );
      onCopied(created);
      onClose();
    } catch (error) {
      captureException(error, { event_id: "lidarClearance.copyAxisLabels" });
      toast.error(t.FailedToCopyLabels);
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => (copying ? undefined : onClose())}
      fullWidth
    >
      <DialogTitle>{t.CopyLabelsDialogTitle}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
      >
        <FormControl fullWidth size="small" disabled={copying}>
          <InputLabel id="copy-labels-axis-label">{t.AxisLabel}</InputLabel>
          <Select
            labelId="copy-labels-axis-label"
            label={t.AxisLabel}
            value={axisId}
            onChange={(event) => selectAxis(Number(event.target.value))}
          >
            {axes.map((axis) => (
              <MenuItem key={axis.id} value={axis.id}>
                {axis.name_en}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={copying || axisId === ""}>
          <InputLabel id="copy-labels-source-system-label">
            {t.CopyFromSystemLabel}
          </InputLabel>
          <Select
            labelId="copy-labels-source-system-label"
            label={t.CopyFromSystemLabel}
            value={sourceSystemId}
            onChange={(event) => setSourceSystemId(Number(event.target.value))}
          >
            {sourceOptions.map((system) => (
              <MenuItem key={system.id} value={system.id}>
                {system.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {axisId !== "" && sourceOptions.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            {t.NoOtherSystemsWithAxis}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={copying}>
          {t.CancelButton}
        </Button>
        <Button
          onClick={handleCopy}
          variant="contained"
          disabled={!canCopy}
          startIcon={
            copying ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t.CopyButton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
